import "./SearchFilter.scss";

import cx from "classnames";
import type { OptionType } from "components/inputs";
import { DatePicker } from "components/inputs";
import { format } from "date-fns";
import { forwardRef, useCallback, useMemo } from "react";
import ReactSelect from "react-select";
import { toggleSearchFilter } from "reducers/main";
import { useGetAdjustersQuery } from "services/adjuster-api";
import { useGetRresQuery } from "services/rre-api";
import { useGetUsersQuery } from "services/users-api";
import { useDispatch, useSelector } from "utils/redux";
import { useUrlStore } from "utils/useUrlStore";

import type {
  BinaryFilterType,
  DateRangeFilterType,
  FilterType,
} from "./useSearchFilters";
import { useSearchFilters } from "./useSearchFilters";

type SearchFilterProps = {
  label: string;
  name: string;
  options: OptionType[];
  addNullOption?: boolean;
};

type SelectOption = {
  label: string;
  value: string;
};

type BinarySearchOptionsProps = {
  options: BinaryFilterType[];
};

type DateRangeFilterProps = {
  label: string;
  nameFrom: string;
  nameTo: string;
};

const SearchFilter = forwardRef<HTMLDivElement, SearchFilterProps>(
  ({ name, options = [], label }, ref) => {
    const { getFilterValues, setFilter, clearFilter } = useUrlStore();

    const selectedValues = getFilterValues(name);

    const formattedOptions = options.map((option) => ({
      label: option.label,
      value: option.value?.toString(),
    }));

    // check if FUTURE_MEDICAL is selected (backend handles expansion)
    const isFutureMedicalSelected =
      name === "service_type" && selectedValues.includes("FUTURE_MEDICAL");

    // display "Future Medical" if FUTURE_MEDICAL is selected, otherwise show actual selected value
    const selectedOptions = isFutureMedicalSelected
      ? formattedOptions.find((option) => option.value === "FUTURE_MEDICAL")
      : formattedOptions.find((option) =>
          selectedValues.includes(option.value),
        ) ||
        (name === "completed_tasks_filter"
          ? formattedOptions.find((option) => option.value === "past_month")
          : null) ||
        null;

    const handleSelectChange = (selected: SelectOption | null) => {
      if (!selected) {
        clearFilter(name);
        return;
      }

      // backend will handle FUTURE_MEDICAL expansion
      setFilter(name, [selected.value]);
    };

    return (
      <div className="panel-section" ref={ref}>
        <h2>{label}</h2>
        <ReactSelect
          options={formattedOptions}
          value={selectedOptions}
          onChange={handleSelectChange}
          placeholder="Select..."
          className="react-select-container"
          classNamePrefix="search-filter-react-select"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 10000,
            }),
            menu: (base) => ({
              ...base,
              zIndex: 10000,
              fontSize: "calc((13px + 0.390625vw) * 0.6)",
            }),
            option: (base, state) => ({
              ...base,
              fontSize: "calc((13px + 0.390625vw) * 0.6)",
              lineHeight: "1.2",
              backgroundColor: state.isSelected
                ? "#3c1283" // $allan-koba
                : state.isFocused
                  ? "rgba(60, 18, 131, 0.1)" // $allan-koba with 10% opacity
                  : base.backgroundColor,
              color: state.isSelected ? "#ffffff" : base.color, // keep default text color for non-selected
              "&:hover": {
                backgroundColor: state.isSelected
                  ? "#2d0e62" // darker $allan-koba
                  : "rgba(60, 18, 131, 0.1)",
                color: state.isSelected ? "#ffffff" : base.color,
              },
            }),
            control: (base, state) => ({
              ...base,
              borderColor: state.isFocused ? "#3c1283" : base.borderColor,
              boxShadow: state.isFocused ? "0 0 0 1px #3c1283" : base.boxShadow,
              "&:hover": {
                borderColor: "#3c1283",
              },
            }),
            dropdownIndicator: (base) => ({
              ...base,
              color: "#3c1283",
              "&:hover": {
                color: "#2d0e62",
              },
            }),
          }}
        />
      </div>
    );
  },
);

SearchFilter.displayName = "SearchFilter";

const DateRangeFilter = ({ label, nameFrom, nameTo }: DateRangeFilterProps) => {
  const { getFilters, setFilter, clearFilter } = useUrlStore();
  const filters = getFilters();
  const fromDate = filters[nameFrom]?.[0]
    ? new Date(filters[nameFrom][0])
    : undefined;
  const toDate = filters[nameTo]?.[0]
    ? new Date(filters[nameTo][0])
    : undefined;

  const handleDateChange = (field: string, date: Date) => {
    if (date) {
      let formattedDate: string;
      if (field === nameTo) {
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        formattedDate = format(endOfDay, "yyyy-MM-dd'T'HH:mm:ss");
      } else {
        formattedDate = format(date, "yyyy-MM-dd'T'HH:mm:ss");
      }
      setFilter(field, [formattedDate]);
    } else {
      clearFilter(field);
    }
  };

  return (
    <div className="panel-section">
      <h2>{label}</h2>
      <div className="date-range-filter">
        <div className="date-input-container">
          <label>From</label>
          <DatePicker
            name={nameFrom}
            initialDate={fromDate}
            onDateChange={(date) => handleDateChange(nameFrom, date)}
            allowFutureDates={true}
          />
        </div>
        <div className="date-input-container">
          <label>To</label>
          <DatePicker
            name={nameTo}
            initialDate={toDate}
            onDateChange={(date) => handleDateChange(nameTo, date)}
            allowFutureDates={true}
          />
        </div>
      </div>
    </div>
  );
};

const BinarySearchOptions = ({ options }: BinarySearchOptionsProps) => {
  const { getFilters, setFilter, clearFilter } = useUrlStore();
  const filters = getFilters();

  const handleChange = useCallback(
    (name: string, checked: boolean) => {
      if (checked) {
        setFilter(name, ["true"]);
      } else {
        clearFilter(name);
      }
    },
    [setFilter, clearFilter],
  );

  return !options?.length ? null : (
    <div className="panel-section">
      <h2>Filters</h2>
      <div className="grid-layout gl-1">
        {options?.map((o: BinaryFilterType, index: number) => {
          const isChecked = filters[o.name]?.[0] === "true";
          return (
            <div
              className="flex items-center gap-2"
              key={index}
              onClick={() => handleChange(o.name, !isChecked)}
            >
              <input
                className="styled-checkbox"
                type="checkbox"
                checked={isChecked}
                onChange={(e) => handleChange(o.name, e.target.checked)}
                aria-label={o.label}
              />
              <label className="cursor-pointer">{o.label}</label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type SearchFilterPanelProps = {
  filterType:
    | "claims"
    | "rres"
    | "your-tasks"
    | "files"
    | "operations"
    | "documents";
  variant?: "header" | "documents";
};

const SearchFilterPanel = forwardRef<HTMLDivElement, SearchFilterPanelProps>(
  ({ filterType = "claims", variant = "header" }, ref) => {
    const searchFilterActive = useSelector(
      (state) => state.main.searchFilterActive,
    );
    const constants = useSelector((state) => state.shared.constants);
    const { clearFilters, getFilters } = useUrlStore();
    const dispatch = useDispatch();

    const {
      data: adjusters = { results: [], count: 0, next: null, previous: null },
    } = useGetAdjustersQuery(
      {},
      {
        skip: filterType !== "claims" && filterType !== "files",
      },
    );

    const {
      data: rres = { results: [], count: 0, next: null, previous: null },
    } = useGetRresQuery(
      {},
      {
        skip:
          filterType !== "claims" &&
          filterType !== "files" &&
          filterType !== "rres",
      },
    );

    const {
      data: users = { results: [], count: 0, next: null, previous: null },
    } = useGetUsersQuery(
      {
        hasClientIntakeAuthority: false,
        userType: "ORGANIZATION",
      },
      { skip: filterType !== "files" },
    );

    const apiResults = useMemo(() => {
      return {
        adjusters,
        rres,
        users,
      };
    }, [adjusters, rres, users]);

    const clearFilterButton = useMemo(() => {
      const filters = getFilters();
      return Object.keys(filters).length > 0 ? (
        <a
          className="button button-icon btn-icon material-icons SearchFilterPanel__ClearButton"
          href=""
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            clearFilters();
            dispatch(toggleSearchFilter());
          }}
        >
          delete
        </a>
      ) : null;
    }, [getFilters, dispatch, clearFilters]);

    const selectFilters = useSearchFilters(filterType, constants, apiResults);

    return (
      <div
        ref={ref}
        id="search-filter-panel"
        className={cx("SearchFilterPanel", {
          active: searchFilterActive,
          "SearchFilterPanel--documents": variant === "documents",
        })}
      >
        <BinarySearchOptions options={selectFilters?.binary || []} />

        {selectFilters?.selects?.map((f: FilterType, index: number) => (
          <SearchFilter
            key={index}
            label={f.label}
            name={f.name}
            options={f.options || []}
            addNullOption={f.addNullOption}
          />
        ))}

        {selectFilters?.dateRanges?.map(
          (dateRange: DateRangeFilterType, index: number) => (
            <DateRangeFilter
              key={index}
              label={dateRange.label}
              nameFrom={dateRange.nameFrom}
              nameTo={dateRange.nameTo}
            />
          ),
        )}

        {clearFilterButton}
      </div>
    );
  },
);

SearchFilterPanel.displayName = "SearchFilterPanel";

export default SearchFilterPanel;
